import { guestPlainQuoteRequestInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { QUOTE_REQUEST_SHARED_FIELD_KEYS, type QuoteRequestSharedFieldKey } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getOrCreateGuestKey } from '@/shared/lib';
import {
  type GuestQuoteRequestDestinationErrors,
  type GuestQuoteRequestFieldErrors,
  type GuestQuoteRequestReceipt,
  type GuestQuoteRequestSubmissionErrors,
  guestQuoteRequestSubmitErrors,
  realClock,
  toGuestQuoteRequestCommonInput,
  type UseSubmitGuestQuoteRequestOptions,
  type UseSubmitGuestQuoteRequestResult,
  validateGuestQuoteRequest,
} from './guest-quote-request-submission';
import type { GuestQuoteRequestFormValues } from './use-submit-guest-quote-request';

/** 비회원 일반 견적 폼 입력 — 디자인 문서·인쇄 방식이 없다. 나머지 칸은 디자인 견적과 같다. */
export type GuestPlainQuoteRequestFormValues = Omit<
  GuestQuoteRequestFormValues,
  'design' | 'printMethodId'
>;

/** 일반 견적이 보여 주는 공용 칸 — 인쇄 방식 칸이 없어 그 오류는 내지 않는다(오더라도 일반 폼 오류다). */
const PLAIN_FIELD_KEYS: readonly QuoteRequestSharedFieldKey[] =
  QUOTE_REQUEST_SHARED_FIELD_KEYS.filter((key) => key !== 'printMethodId');

/**
 * 비회원 일반 견적 요청 제출(quotes.submitGuestPlainRequest, 일반 견적 설계 §5-3) — 편집영역 없는 상품을 디자인 문서·인쇄 방식 없이
 * 요청한다. 검증·칸 오류·한도 초과·서버 거절 처리는 디자인 견적(`useSubmitGuestQuoteRequest`)과 같은 공용 모듈을 쓴다.
 */
export const useSubmitGuestPlainQuoteRequest = ({
  minOrderQty,
  priceTiers,
  shippingFeeSettings,
  sizeOptions,
  now = realClock,
  readGuestKey = getOrCreateGuestKey,
}: UseSubmitGuestQuoteRequestOptions): UseSubmitGuestQuoteRequestResult<GuestPlainQuoteRequestFormValues> => {
  const [fieldErrors, setFieldErrors] = useState<GuestQuoteRequestFieldErrors>({});
  const [destinationErrors, setDestinationErrors] = useState<GuestQuoteRequestDestinationErrors>(
    {},
  );
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = trpc.quotes.submitGuestPlainRequest.useMutation();

  const show = (errors: GuestQuoteRequestSubmissionErrors): void => {
    setFieldErrors(errors.fieldErrors);
    setDestinationErrors(errors.destinationErrors);
    setFormError(errors.formError);
  };

  const submit = async (
    values: GuestPlainQuoteRequestFormValues,
  ): Promise<GuestQuoteRequestReceipt | null> => {
    const validation = validateGuestQuoteRequest(
      guestPlainQuoteRequestInputSchema.safeParse(
        toGuestQuoteRequestCommonInput(values, readGuestKey()),
      ),
      values,
      { minOrderQty, priceTiers, now: now() },
      shippingFeeSettings,
      PLAIN_FIELD_KEYS,
      sizeOptions,
    );
    show(validation);

    if (validation.input === null) {
      return null;
    }

    try {
      const result = await mutation.mutateAsync(validation.input);
      return { ...result, contactMethod: values.contactMethod };
    } catch (error) {
      show(guestQuoteRequestSubmitErrors(error, values, PLAIN_FIELD_KEYS));
      return null;
    }
  };

  return { fieldErrors, destinationErrors, formError, isPending: mutation.isPending, submit };
};
